/**
 * DocumentChangeButton - Button to open the visual change tracking system
 * 
 * Simple integration component that can be added to existing document views
 * to provide access to the Google Docs-style change tracking interface.
 */

import React, { useState } from 'react';
import { ClockIcon } from '@heroicons/react/24/outline';
import { DocumentChangeViewerModal } from './DocumentChangeViewer';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface DocumentChangeButtonProps {
  documentId: string;
  versionId?: string;
  documentContent: string;
  documentTitle?: string;
  className?: string;
  variant?: 'button' | 'icon' | 'text';
  size?: 'sm' | 'md' | 'lg';
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const DocumentChangeButton: React.FC<DocumentChangeButtonProps> = ({
  documentId,
  versionId,
  documentContent,
  documentTitle,
  className = '',
  variant = 'button',
  size = 'md'
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Size classes
  const sizeClasses = {
    sm: {
      button: 'px-2 py-1 text-xs',
      icon: 'h-4 w-4',
      text: 'text-xs'
    },
    md: {
      button: 'px-3 py-2 text-sm',
      icon: 'h-5 w-5',
      text: 'text-sm'
    },
    lg: {
      button: 'px-4 py-2 text-base',
      icon: 'h-6 w-6',
      text: 'text-base'
    }
  };

  // Render different variants
  const renderButton = () => {
    const baseClasses = "inline-flex items-center space-x-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors";
    
    switch (variant) {
      case 'icon':
        return (
          <button
            onClick={handleOpenModal}
            className={`${baseClasses} p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${className}`}
            title="View document changes"
          >
            <ClockIcon className={sizeClasses[size].icon} />
          </button>
        );
      
      case 'text':
        return (
          <button
            onClick={handleOpenModal}
            className={`${baseClasses} ${sizeClasses[size].text} ${className}`}
          >
            <ClockIcon className={sizeClasses[size].icon} />
            <span>Version History</span>
          </button>
        );
      
      default: // 'button'
        return (
          <button
            onClick={handleOpenModal}
            className={`${baseClasses} ${sizeClasses[size].button} bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm ${className}`}
          >
            <ClockIcon className={sizeClasses[size].icon} />
            <span>Version History</span>
          </button>
        );
    }
  };

  return (
    <>
      {renderButton()}
      
      <DocumentChangeViewerModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        documentId={documentId}
        versionId={versionId}
        documentContent={documentContent}
        documentTitle={documentTitle}
      />
    </>
  );
};

// ============================================================================
// HOOK FOR INTEGRATION
// ============================================================================

/**
 * Hook to easily integrate change tracking into existing components
 */
export const useDocumentChangeTracking = (
  documentId: string,
  documentContent: string,
  documentTitle?: string,
  versionId?: string
) => {
  const [isChangeViewerOpen, setIsChangeViewerOpen] = useState(false);

  const openChangeViewer = () => setIsChangeViewerOpen(true);
  const closeChangeViewer = () => setIsChangeViewerOpen(false);

  const ChangeViewerModal = () => (
    <DocumentChangeViewerModal
      isOpen={isChangeViewerOpen}
      onClose={closeChangeViewer}
      documentId={documentId}
      versionId={versionId}
      documentContent={documentContent}
      documentTitle={documentTitle}
    />
  );

  return {
    openChangeViewer,
    closeChangeViewer,
    isChangeViewerOpen,
    ChangeViewerModal
  };
};

export default DocumentChangeButton;
